import { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { FileWithPath } from 'react-dropzone';
import { useQueryClient } from 'shared-ui';

import { useMutation } from 'hooks/useMutation/useMutation';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useQuery } from 'hooks/useQuery/useQuery';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { QueryKeyEnum } from 'core/global.enum';
import {
  getAutoExportConfig,
  getAutoExportStatus,
  updateAutoExportConfig,
  uploadKluczPrywatny,
  startAutoExport,
  stopAutoExport,
} from 'api/autoExport/autoExport';
import { AutoExportConfig } from 'api/autoExport/autoExport.types';
import { FormatTyp, TypInterwalu, RcpFiltr } from 'api/autoExport/autoExport.enum';
import { Organization } from 'api/organizations/organizations.types';
import { TreeSelectOption } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect.types';

import { Automation } from './Automation';
import { AutomationFormBody, PASSWORD_PLACEHOLDER } from './Automation.types';
import { useAutomationFormValidation } from './Automation.validation';

const MAX_KEY_SIZE = 16 * 1024;

const findOrgInTree = (orgs: Organization[], id: string): Organization | undefined => {
  for (const org of orgs) {
    if (org.id === id) return org;
    if (org.children) {
      const found = findOrgInTree(org.children, id);
      if (found) return found;
    }
  }

  return undefined;
};

const resolveKomorki = (ids: string[] | null, organizations: Organization[]): TreeSelectOption[] => {
  if (!ids?.length) return [];

  return ids.map((id) => {
    const org = findOrgInTree(organizations, id);

    return { id, nazwa: org?.nazwa ?? '' };
  });
};

export const AutomationContainer = () => {
  const { formatMessage } = useLocale();
  const { schema } = useAutomationFormValidation();
  const { perms } = usePermissions();
  const canView = !!perms?.DEFINICJE?.view;
  const queryClient = useQueryClient();
  const savedDraft = useRef(sessionStorage.getItem('automation-form-draft'));
  const [isEditing, setIsEditing] = useState(!!savedDraft.current);
  const [privateKeyFile, setPrivateKeyFile] = useState<FileWithPath | undefined>();
  const [privateKeyError, setPrivateKeyError] = useState<string | undefined>();
  const [startError, setStartError] = useState<string | null>(null);

  const defaultValues: AutomationFormBody = {
    adres: '',
    port: '',
    nazwaUzytkownika: '',
    haslo: '',
    katalog: '',
    formatTyp: '' as FormatTyp | '',
    nazwaPliku: '',
    typInterwalu: '' as TypInterwalu | '',
    interwalMinuty: '',
    harmonogramCzas1: null,
    harmonogramCzas2: null,
    harmonogramCzas3: null,
    stempelDaty: true,
    rcpFiltr: RcpFiltr.WSZYSTKIE,
    komorki: [],
  };

  const responseToFormBody = (data: AutoExportConfig, orgs: Organization[] = []): AutomationFormBody => {
    const times = data.harmonogramCzasy || [];

    return {
      adres: data.adres || '',
      port: data.port != null ? String(data.port) : '',
      nazwaUzytkownika: data.nazwaUzytkownika || '',
      haslo: data.hasPassword ? PASSWORD_PLACEHOLDER : '',
      katalog: data.katalog || '',
      formatTyp: data.formatTyp || ('' as FormatTyp | ''),
      nazwaPliku: data.nazwaPliku || '',
      typInterwalu: data.typInterwalu || ('' as TypInterwalu | ''),
      interwalMinuty: data.interwalMinuty != null ? String(data.interwalMinuty) : '',
      harmonogramCzas1: times[0] ? new Date(`1970-01-01T${times[0]}`) : null,
      harmonogramCzas2: times[1] ? new Date(`1970-01-01T${times[1]}`) : null,
      harmonogramCzas3: times[2] ? new Date(`1970-01-01T${times[2]}`) : null,
      stempelDaty: data.stempelDaty,
      rcpFiltr: data.rcpFiltr,
      komorki: resolveKomorki(data.komorkaIds, orgs),
    };
  };

  const autoExportConfig = queryClient.getQueryData<AutoExportConfig>([QueryKeyEnum.AUTO_EXPORT_CONFIG]);
  // WKD-2453: read from the unfiltered ORGANIZATIONS cache (matches the
  // useFilteredData={false} OrganizationsTreeSelect prop in Automation.tsx).
  const organizations = queryClient.getQueryData<Organization[]>([QueryKeyEnum.ORGANIZATIONS]) || [];

  const formMethods = useForm<AutomationFormBody>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: autoExportConfig ? responseToFormBody(autoExportConfig, organizations) : defaultValues,
  });

  // Restore form draft from sessionStorage (saved before navigating to format preview)
  useEffect(() => {
    if (savedDraft.current) {
      try {
        const parsed = JSON.parse(savedDraft.current);
        const restored: AutomationFormBody = {
          ...parsed,
          harmonogramCzas1: parsed.harmonogramCzas1 ? new Date(parsed.harmonogramCzas1) : null,
          harmonogramCzas2: parsed.harmonogramCzas2 ? new Date(parsed.harmonogramCzas2) : null,
          harmonogramCzas3: parsed.harmonogramCzas3 ? new Date(parsed.harmonogramCzas3) : null,
        };
        formMethods.reset(restored);
      } finally {
        sessionStorage.removeItem('automation-form-draft');
        savedDraft.current = null;
      }
    }
  }, []);

  const { isLoading: isLoadingConfig } = useQuery([QueryKeyEnum.AUTO_EXPORT_CONFIG], getAutoExportConfig, {
    enabled: canView,
    onSuccess: (data) => {
      if (isEditing) return;
      // WKD-2453: use the unfiltered ORGANIZATIONS query (not the per-user
      // filtered one) so resolveKomorki can find every komorka the config
      // references — even ones the current operator doesn't personally have
      // access to.
      const orgs = queryClient.getQueryData<Organization[]>([QueryKeyEnum.ORGANIZATIONS]) || [];
      formMethods.reset(responseToFormBody(data, orgs));
    },
  });

  const { mutate: onUpdateConfig, isLoading } = useMutation(updateAutoExportConfig, {
    onSuccess: () => {
      toast.dark(formatMessage({ id: AppMessages['definitions.automation.success'] }));
      setIsEditing(false);
      setStartError(null);
      queryClient.invalidateQueries([QueryKeyEnum.AUTO_EXPORT_CONFIG]);
    },
  });

  const { data: exportStatus } = useQuery([QueryKeyEnum.AUTO_EXPORT_STATUS], getAutoExportStatus, {
    enabled: canView,
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
  });

  const isRunning = exportStatus?.state === 'STARTED';

  const isConfigComplete = !!(
    autoExportConfig?.adres &&
    autoExportConfig?.port &&
    autoExportConfig?.nazwaUzytkownika &&
    autoExportConfig?.nazwaPliku &&
    autoExportConfig?.formatTyp &&
    autoExportConfig?.typInterwalu &&
    (autoExportConfig?.hasPassword || autoExportConfig?.hasPrivateKey)
  );

  const { mutate: onStartExport, isLoading: isStarting } = useMutation(startAutoExport, {
    onMutate: () => setStartError(null),
    onSuccess: () => {
      setStartError(null);
      queryClient.invalidateQueries([QueryKeyEnum.AUTO_EXPORT_STATUS]);
      queryClient.invalidateQueries([QueryKeyEnum.AUTO_EXPORT_CONFIG]);
    },
    onError: () => {
      setStartError(formatMessage({ id: AppMessages['definitions.automation.lifecycle.startError'] }));
    },
  });

  const { mutate: onStopExport, isLoading: isStopping } = useMutation(stopAutoExport, {
    onSuccess: () => {
      toast.dark(formatMessage({ id: AppMessages['definitions.automation.lifecycle.stopSuccess'] }));
      queryClient.invalidateQueries([QueryKeyEnum.AUTO_EXPORT_STATUS]);
      queryClient.invalidateQueries([QueryKeyEnum.AUTO_EXPORT_CONFIG]);
    },
  });

  const { mutate: onUploadKey, isLoading: isValidatingFile } = useMutation(uploadKluczPrywatny, {
    onMutate: () => setPrivateKeyError(undefined),
    onSuccess: () => {
      setPrivateKeyError(undefined);
      queryClient.invalidateQueries([QueryKeyEnum.AUTO_EXPORT_CONFIG]);
    },
    onError: () => {
      setPrivateKeyFile(undefined);
      setPrivateKeyError(formatMessage({ id: AppMessages['definitions.automation.privateKey.invalid'] }));
    },
  });

  const onEditClick = () => {
    setIsEditing(true);
  };

  const onCancelClick = () => {
    setIsEditing(false);
    setPrivateKeyError(undefined);
    setPrivateKeyFile(undefined);
    if (autoExportConfig) {
      // WKD-2453: matches the unfiltered ORGANIZATIONS cache used elsewhere
      // in this container so cancel restores the form with the same komorka
      // labels the user saw before edit.
      const orgs = queryClient.getQueryData<Organization[]>([QueryKeyEnum.ORGANIZATIONS]) || [];
      formMethods.reset(responseToFormBody(autoExportConfig, orgs));
    } else {
      formMethods.reset(defaultValues);
    }
  };

  const onPrivateKeyDrop = (files: FileWithPath[]) => {
    if (files.length > 0) {
      const file = files[0];

      // WKD-2442: defensive .txt check — Safari ignores the dropzone `accept` map
      // and forwards any file type. We reject anything that does not end in `.txt`.
      if (!file.name.toLowerCase().endsWith('.txt')) {
        setPrivateKeyError(formatMessage({ id: AppMessages['definitions.automation.privateKey.invalidExtension'] }));
        return;
      }

      if (file.size > MAX_KEY_SIZE) {
        setPrivateKeyError(formatMessage({ id: AppMessages['definitions.automation.privateKey.tooLarge'] }));
        return;
      }

      setPrivateKeyFile(file);
      setPrivateKeyError(undefined);
    }
  };

  const onPrivateKeyRemove = () => {
    setPrivateKeyFile(undefined);
    setPrivateKeyError(undefined);
  };

  useEffect(() => {
    if (privateKeyFile) {
      const formData = new FormData();
      formData.append('klucz', privateKeyFile);
      onUploadKey(formData);
    }
  }, [privateKeyFile]);

  // WKD-2441: edits to address/port/username put the form into "credentials need
  // refresh" mode. The stored private key is dropped from the DB on save unless
  // the operator uploads a new one in the same edit session.
  const { dirtyFields } = formMethods.formState;
  const keyDirty = Boolean(dirtyFields.adres || dirtyFields.port || dirtyFields.nazwaUzytkownika);

  const onSubmit = (body: AutomationFormBody) => {
    const { harmonogramCzas1, harmonogramCzas2, harmonogramCzas3, komorki, haslo, ...rest } = body;

    const formatTime = (date: Date | null): string | undefined => {
      if (!date) return undefined;
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const harmonogramCzasy = [
      formatTime(harmonogramCzas1),
      formatTime(harmonogramCzas2),
      formatTime(harmonogramCzas3),
    ].filter((t): t is string => !!t);

    // WKD-2441: clear stored key only when (a) the address/port/user changed,
    // (b) the operator did NOT upload a fresh key in this edit session, AND
    // (c) there was a key stored to begin with. Sending `null` triggers the
    // BE clear path on PUT /config.
    const shouldClearStoredKey = keyDirty && !privateKeyFile && !!autoExportConfig?.hasPrivateKey;

    // WKD-2432 (reopen): map the password field's three intents to the BE
    // contract without adding new UI.
    //   - PASSWORD_PLACEHOLDER (field untouched) → omit haslo (no change)
    //   - empty / whitespace-only AFTER the operator focused the field, and a
    //     password was previously stored → send `haslo: null` so the BE clears
    //     it. Mirrors the kluczPrywatny=null pattern from WKD-2441 and is the
    //     ONLY supported way to switch a password-only config to key-only
    //     auth from the current UI.
    //   - any other non-empty value → send as-is (BE encrypts).
    // Also catches the historical footgun where a whitespace-only submission
    // hit the BE and 400'd with "Hasło nie może składać się wyłącznie z
    // białych znaków" — we now translate that intent locally.
    const hasloField = ((): { haslo: string | null } | Record<string, never> => {
      if (haslo === PASSWORD_PLACEHOLDER) return {};
      if (haslo.trim() === '') {
        return autoExportConfig?.hasPassword ? { haslo: null } : {};
      }
      return { haslo };
    })();

    onUpdateConfig({
      ...rest,
      typInterwalu: rest.typInterwalu as TypInterwalu,
      formatTyp: rest.formatTyp as FormatTyp,
      port: parseInt(body.port, 10),
      interwalMinuty: body.interwalMinuty ? parseInt(body.interwalMinuty, 10) : null,
      harmonogramCzasy: harmonogramCzasy.length > 0 ? harmonogramCzasy : null,
      komorkaIds: komorki?.map((k) => k.id) || null,
      ...hasloField,
      ...(shouldClearStoredKey ? { kluczPrywatny: null } : {}),
    });
  };

  return (
    <FormProvider {...formMethods}>
      <Automation
        onSubmit={onSubmit}
        isLoading={isLoading || isLoadingConfig}
        isEditing={isEditing}
        isActive={isRunning}
        lastError={startError ?? exportStatus?.ostatniBlad ?? null}
        isConfigComplete={isConfigComplete}
        isLifecycleLoading={isStarting || isStopping}
        onStartExport={() => onStartExport(undefined)}
        onStopExport={() => onStopExport(undefined)}
        onEditClick={onEditClick}
        onCancelClick={onCancelClick}
        privateKeyFile={privateKeyFile}
        onPrivateKeyDrop={onPrivateKeyDrop}
        onPrivateKeyRemove={onPrivateKeyRemove}
        privateKeyError={privateKeyError}
        setPrivateKeyError={setPrivateKeyError}
        isValidatingFile={isValidatingFile}
        hasPassword={autoExportConfig?.hasPassword ?? false}
        hasPrivateKey={autoExportConfig?.hasPrivateKey ?? false}
        keyDirty={keyDirty}
      />
    </FormProvider>
  );
};
